/* eslint-disable react-hooks/exhaustive-deps */
import React, { useEffect, useState } from "react";
import {
  Container,
  Card,
  Button,
  FormControl,
  InputLabel,
  Select,
  MenuItem,
  Box,
  Divider,
  Typography,
  Grid,
} from "@mui/material";
import type { SelectChangeEvent } from "@mui/material/Select";
import { useNavigate, useParams } from "react-router-dom";
import Header, { HeaderType } from "../../components/Header";
import { GetVehicleList } from "../../services/api/vehicle-service";
import { getAccessToken, useAuth } from "../../context/auth";
import type { CreateUpdateVehicleDto } from "../../services/model/vehicle";
import { NavigateTo, getNavigation } from "../../helper/navigation";
import { v4 as uuidv4 } from "uuid";
import { useConfirm } from "../../components/dialog/confirm";
import BrandLoader from "@/branding/BrandLoader";

interface AccidentSupportProps {
  style?: any;
  loading: boolean;
}

interface ISelectData {
  name: string;
  id: string;
  key: number;
}

const AccidentSupport: React.FC<AccidentSupportProps> = (_props) => {
  const confirm = useConfirm();
  const { authTokens } = useAuth();
  const token = getAccessToken(authTokens) ?? "";
  const [vehicle, setVehicle] = React.useState<string>("");
  const [vehicles, setVehicles] = useState<ISelectData[]>([]);
  const navigate = useNavigate();
  const { tenant } = useParams<{ tenant: string }>();
  const [loading, setLoading] = useState(false);

  // helper to keep all routes tenant-aware
  const go = (
    path: string,
    opts?: import("react-router-dom").NavigateOptions
  ) => {
    const clean = path.replace(/^\/+/, ""); // remove leading slash
    navigate(`/${tenant}/${clean}`, opts);
  };

  const handleChange = (event: SelectChangeEvent<string>) => {
    setVehicle(event.target.value || "");
  };

  const cancel = () => {
    navigate(`/${tenant}/accidentSupportList`);
  };

  const gotToNextScreen = () => {
    if (!vehicle) {
      showDialog("No Vehicle Selected", "Please select a vehicle to continue.");
      return;
    }

    localStorage.setItem(
      "VehicleId",
      JSON.stringify(vehicle || "00000000-0000-0000-0000-000000000000")
    );

    localStorage.removeItem("AccidentForm");

    const next = NavigateTo();
    go(next);
  };

  const goToVehicleAdd = () => {
    localStorage.removeItem("viewVehicleId");
    localStorage.setItem("vehicleReturnTo", "accidentsupport");
    localStorage.setItem("AccidentForm", "1"); // optional legacy
    go("vehicleAddEdit");
  };

  const skipStep = () => {
    confirm({
      title: "No Vehicles",
      description:
        "There are no vehicles linked to your profile, please add one to continue.",
      okOnly: false,
      catchOnCancel: true,
    })
      .then(() => goToVehicleAdd())
      .catch(() => cancel());
  };

  const showDialog = (title: string, message: string) => {
    confirm({ title, description: message });
  };

  const fetchVehicle = async () => {
    try {
      setLoading(true);
      const response = await GetVehicleList(token);
      if (!response) {
        setLoading(false);
        showDialog(
          "Failure",
          "There seems to be a problem with your internet connection.\nPlease check your internet connection."
        );
        return;
      }

      const vehicleResponse = (response.data as CreateUpdateVehicleDto[]) ?? [];
      if (vehicleResponse.length === 0) {
        setLoading(false);
        skipStep();
        return;
      }

      // Build a clean list (no pre-sized array + push)
      const list: ISelectData[] = vehicleResponse.map((v, i) => ({
        key: i,
        name: String(v.licensePlateNumber ?? ""),
        id: String(v.id ?? ""),
      }));

      setVehicles(list);
      setLoading(false);
    } catch (err) {
      setLoading(false);
      showDialog(
        "Failure",
        "An exception occurred, please contact your administrator."
      );
      if (err instanceof Error) console.error(err.message);
    }
  };

  const generateAccidentSupportId = () => {
    const temp = window.localStorage.getItem("accidentId") || "";
    if (!temp) {
      const accidentId = uuidv4();
      localStorage.setItem("accidentId", JSON.stringify(accidentId));
    }
  };

  useEffect(() => {
    localStorage.removeItem("formTypeId");
    localStorage.removeItem("ListId");
    localStorage.removeItem("currentScreenIndex");
    localStorage.removeItem("navigation");
    localStorage.removeItem("accidentId");
    localStorage.removeItem("viewAccidentSupportID");
    localStorage.removeItem("Risk Address");

    generateAccidentSupportId();
    fetchVehicle();
    getNavigation(token);
  }, []);

  return (
    <div>
      <Header type={HeaderType.AccidentSupportAdd} />
      <Container maxWidth="sm">
        <Grid container spacing={1}>
          <Grid size={12}>
            <Card variant="outlined" sx={{ p: 2 }}>
              <Grid
                container
                direction="row"
                justifyContent="center"
                spacing={1}
              >
                <Typography variant="subtitle1">
                  In which vehicle did the accident occur?
                </Typography>

                <FormControl variant="outlined" fullWidth>
                  <InputLabel id="vehicle-label">Select Vehicle</InputLabel>
                  <Select
                    labelId="vehicle-label"
                    id="vehicle-select"
                    value={vehicle}
                    onChange={handleChange}
                    label="Select Vehicle"
                    required
                  >
                    {vehicles.map((v) => (
                      <MenuItem key={v.key} value={v.id}>
                        {v.name}
                      </MenuItem>
                    ))}
                  </Select>
                </FormControl>
              </Grid>

              <Divider sx={{ pb: 7 }} />

              <Box display="flex" flexDirection="column" gap={3}>
                <Button
                  onClick={goToVehicleAdd}
                  value="next"
                  variant="contained"
                  color="primary"
                  fullWidth
                  size="large"
                >
                  <strong>Add New Vehicle</strong>
                </Button>

                <Button
                  onClick={gotToNextScreen}
                  value="next"
                  variant="contained"
                  color="secondary"
                  fullWidth
                  size="large"
                >
                  <strong>Next</strong>
                </Button>

                <Button
                  onClick={cancel}
                  value="next"
                  variant="outlined"
                  color="secondary"
                  fullWidth
                  size="large"
                >
                  <strong>Back</strong>
                </Button>
              </Box>
            </Card>
          </Grid>
        </Grid>
      </Container>

      <BrandLoader open={loading} />
    </div>
  );
};

export default AccidentSupport;
